import { useTranslate } from '@tolgee/react';
import { GetServerSideProps } from 'next';
import { Session } from 'next-auth';
import { getSession } from 'next-auth/react';
import styled from 'styled-components';

import LandingPageHome from '../../components/LandingPageHome';
import { NextPageWithLayout } from '../../components/layouts';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import useRoutePrefetch from '../../hooks/useRoutePrefetch';
import { DefaultLocale, SupportedLocales } from '../../providers/tolgee';
import CoreAPIService from '../../services/CoreAPIService';

interface LocalizedLandingProps {
  locale: string;
}

const LocalizedLanding: NextPageWithLayout<LocalizedLandingProps> = ({ locale }) => {
  const { t } = useTranslate();

  // Prefetch routes for authenticated users
  useRoutePrefetch('/[tenantId]/lessons');
  useRoutePrefetch('/[tenantId]/workspace/[workspaceId]');

  // Language is set via SSR prop on TolgeeProvider, no need to set it manually

  return (
    <Container>
      <LandingPageLayout
        title={t(
          'meta.landing.title',
          'Curipod - Interactive Lessons for K-12 that Students Love',
        )}
        subTitle={t(
          'meta.landing.description',
          'Experience the joy of every student participating, engaging, and learning with instant lessons for every subject.',
        )}
        locale={locale}
        hideBlobs={true}
        disableHeadline={true}
      >
        <LandingPageHome />
      </LandingPageLayout>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
`;

const TIMEOUT = 20000;
export const getServerSideProps: GetServerSideProps = async context => {
  try {
    const locale = context.params?.locale as string;

    // Validate against supported locales
    if (!SupportedLocales.includes(locale)) {
      return { notFound: true };
    }

    // Check for authenticated users and redirect them
    const sessionPromise = getSession({ req: context.req });
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), TIMEOUT),
    );

    const session = (await Promise.race([
      sessionPromise,
      timeoutPromise,
    ])) as Session | null;

    if (session) {
      const { activeTenantId } = await CoreAPIService.fetchActiveTenantId(
        session.user.uid,
      );

      return {
        redirect: {
          permanent: false,
          destination: `/${activeTenantId}/lessons`,
        },
      };
    }

    // Set server-side cache headers for performance
    context.res.setHeader(
      'Cache-Control',
      'public, s-maxage=3600, stale-while-revalidate=86400',
    );

    // Prepare SSR data for TolgeeProvider
    const tolgeeSSR = {
      language: locale,
    };

    return {
      props: {
        locale,
        _tolgeeSSR: tolgeeSSR,
      },
    };
  } catch (error) {
    return {
      props: {
        locale: DefaultLocale,
        _tolgeeSSR: {
          language: DefaultLocale,
        },
      },
    };
  }
};

LocalizedLanding.getLayout = function getLayout(page) {
  return <>{page}</>;
};

export default LocalizedLanding;
